import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import userService from '../utils/services/UserService';

/**
 * React hook for user management operations
 * Provides state management, caching, and real-time updates for user data
 */
export const useUserService = () => {
  const { currentUser, currentUserData } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const unsubscribeRef = useRef(null);

  /**
   * Get all users (admin only)
   */
  const getAllUsers = useCallback(async (useCache = true) => {
    if (!currentUserData?.isAdmin) {
      setUsers([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const allUsers = await userService.getAllUsers(useCache);
      setUsers(allUsers);
      return allUsers;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [currentUserData?.isAdmin]);

  /**
   * Get current user data
   */
  const getCurrentUser = useCallback(async (useCache = true) => {
    if (!currentUser?.uid) {
      return null;
    }

    setError(null);

    try {
      const user = await userService.getUser(currentUser.uid, useCache);
      return user;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [currentUser?.uid]);

  /**
   * Get user by ID
   */
  const getUser = useCallback(async (uid, useCache = true) => {
    if (!uid) {
      return null;
    }

    setError(null);

    try {
      const user = await userService.getUser(uid, useCache);
      return user;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, []);

  /**
   * Get user by email
   */
  const getUserByEmail = useCallback(async (email, useCache = true) => {
    if (!email) {
      return null;
    }

    setError(null);

    try {
      const user = await userService.getUserByEmail(email, useCache);
      return user;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, []);

  /**
   * Create a new user
   */
  const createUser = useCallback(async (userData) => {
    setError(null);

    try {
      const newUser = await userService.createUser(userData);
      
      // Update local state if admin
      if (currentUserData?.isAdmin) {
        setUsers(prevUsers => [newUser, ...prevUsers]);
      }
      
      return newUser;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentUserData?.isAdmin]);

  /**
   * Update a user
   */
  const updateUser = useCallback(async (uid, updateData) => {
    setError(null);

    try {
      const updatedUser = await userService.updateUser(uid, updateData);
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.key === uid ? updatedUser : user
        )
      );
      
      return updatedUser;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a user
   */
  const deleteUser = useCallback(async (uid, email = null, username = null) => {
    setError(null);

    try {
      await userService.deleteUser(uid, email, username);
      
      // Update local state
      setUsers(prevUsers => prevUsers.filter(user => user.key !== uid));
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Get admin users
   */
  const getAdminUsers = useCallback(async (useCache = true) => {
    try {
      return await userService.getAdminUsers(useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  /**
   * Get demo users
   */
  const getDemoUsers = useCallback(async (useCache = true) => {
    try {
      return await userService.getDemoUsers(useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  /**
   * Get test users
   */
  const getTestUsers = useCallback(async (useCache = true) => {
    try {
      return await userService.getTestUsers(useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  /**
   * Get users with expired trials
   */
  const getExpiredTrialUsers = useCallback(async (useCache = true) => {
    try {
      return await userService.getExpiredTrialUsers(useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  /**
   * Get users with active trials
   */
  const getActiveTrialUsers = useCallback(async (useCache = true) => {
    try {
      return await userService.getActiveTrialUsers(useCache);
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  /**
   * Update user trial data
   */
  const updateTrialData = useCallback(async (uid, trialData) => {
    setError(null);

    try {
      const updatedUser = await userService.updateTrialData(uid, trialData);
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.key === uid ? updatedUser : user
        )
      );
      
      return updatedUser;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Extend user trial
   */
  const extendTrial = useCallback(async (uid, days) => {
    setError(null);

    try {
      const updatedUser = await userService.extendTrial(uid, days);
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.key === uid ? updatedUser : user
        )
      );
      
      return updatedUser;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Get user statistics
   */
  const getUserStats = useCallback(async (useCache = true) => {
    if (!currentUserData?.isAdmin) {
      setStats(null);
      return null;
    }

    setError(null);

    try {
      const userStats = await userService.getUserStats(useCache);
      setStats(userStats);
      return userStats;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [currentUserData?.isAdmin]);

  /**
   * Set up real-time listener for all users (admin only)
   */
  const subscribeToUsers = useCallback(() => {
    if (!currentUserData?.isAdmin) return;

    // Clean up existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    try {
      unsubscribeRef.current = userService.subscribeToAllUsers(
        (updatedUsers, changes) => {
          setUsers(updatedUsers);
          
          // Log changes for debugging
          if (changes.length > 0) {
          }
        }
      );
    } catch (err) {
      setError(err.message);
      console.error('Error setting up user listener:', err);
    }
  }, [currentUserData?.isAdmin]);

  /**
   * Set up real-time listener for current user
   */
  const subscribeToCurrentUser = useCallback((callback) => {
    if (!currentUser?.uid) return;

    try {
      return userService.subscribeToUser(currentUser.uid, callback);
    } catch (err) {
      setError(err.message);
      console.error('Error setting up current user listener:', err);
    }
  }, [currentUser?.uid]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    userService.clearCache();
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    return userService.getCacheStats();
  }, []);

  /**
   * Log index creation instructions
   */
  const logIndexInstructions = useCallback(() => {
    userService.logIndexInstructions();
  }, []);

  /**
   * Debug method to check raw data
   */
  const debugGetAllUsers = useCallback(async (uid = null) => {
    try {
      return await userService.debugGetAllUsers(uid);
    } catch (error) {
      console.error('Debug error:', error);
      return [];
    }
  }, []);

  /**
   * Batch update users
   */
  const batchUpdateUsers = useCallback(async (operations) => {
    setError(null);

    try {
      await userService.batchUpdateUsers(operations);
      
      // Refresh users list
      await getAllUsers(false);
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [getAllUsers]);

  // Auto-subscribe to users when admin status changes
  useEffect(() => {
    if (currentUserData?.isAdmin) {
      // Initial load
      getAllUsers();
      getUserStats();
      
      // Set up real-time listener
      subscribeToUsers();
    } else {
      setUsers([]);
      setStats(null);
    }
  }, [currentUserData?.isAdmin, getAllUsers, getUserStats, subscribeToUsers]);

  return {
    // State
    users,
    loading,
    error,
    stats,
    
    // Actions
    getAllUsers,
    getCurrentUser,
    getUser,
    getUserByEmail,
    createUser,
    updateUser,
    deleteUser,
    getAdminUsers,
    getDemoUsers,
    getTestUsers,
    getExpiredTrialUsers,
    getActiveTrialUsers,
    updateTrialData,
    extendTrial,
    getUserStats,
    subscribeToUsers,
    subscribeToCurrentUser,
    clearError,
    clearCache,
    getCacheStats,
    batchUpdateUsers,
    
    // Computed values
    adminUsers: users.filter(user => user.isAdmin),
    demoUsers: users.filter(user => user.demoUser),
    testUsers: users.filter(user => user.testUser),
    expiredTrialUsers: users.filter(user => user.trialExpired),
    activeTrialUsers: users.filter(user => user.trialActive),
    emailUnsubscribedUsers: users.filter(user => user.emailUnsubscribed),
    // Email subscription methods
    getUserEmailSubscription: userService.getUserEmailSubscription.bind(userService),
    setUserEmailSubscription: userService.setUserEmailSubscription.bind(userService),
    subscribeUserToEmails: userService.subscribeUserToEmails.bind(userService),
    unsubscribeUserFromEmails: userService.unsubscribeUserFromEmails.bind(userService),
    getSubscribedUsers: userService.getSubscribedUsers.bind(userService),
    getUnsubscribedUsers: userService.getUnsubscribedUsers.bind(userService),
    getUsersWithEmailSubscriptionStatus: userService.getUsersWithEmailSubscriptionStatus.bind(userService),
    
    // Utility functions
    logIndexInstructions,
    debugGetAllUsers
  };
};

export default useUserService; 